/**
 * Action: tag_group  (+ inverse: untag_group)
 *
 * Apply / remove a single tag to/from a group. Mirrors tag_person
 * exactly — the `taggings` table is polymorphic via
 * (taggable_type, taggable_id); tag_person uses taggable_type
 * "Person", this uses "Group". Verified against prod: no CHECK
 * constraint on taggable_type, so "Group" is accepted.
 *
 * Pairs with the focused-group context (#1784): on a GroupDetail
 * page the planner has `focusedGroupId` and "tag this group as
 * X" lands the right group_id automatically.
 */

import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { logAudit } from "@/lib/audit";
import { registerAction } from "../registry";
import type { ActionDefinition, ActionResult } from "../types";

const tagGroupParams = z.object({
  group_id: z.string().guid(),
  tag_id: z.string().guid(),
});
type TagGroupParams = z.infer<typeof tagGroupParams>;

async function resolveGroupAndTag(
  organizationId: string,
  groupId: string,
  tagId: string,
): Promise<{ groupName: string; tagName: string }> {
  const [groupResult, tagResult] = await Promise.all([
    supabase
      .from("groups")
      .select("id, name")
      .eq("organization_id", organizationId)
      .eq("id", groupId)
      .maybeSingle(),
    supabase
      .from("tags")
      .select("id, name")
      .eq("organization_id", organizationId)
      .eq("id", tagId)
      .maybeSingle(),
  ]);

  if (groupResult.error || !groupResult.data) {
    throw new Error("Group not found in this organization.");
  }
  if (tagResult.error || !tagResult.data) {
    throw new Error("Tag not found in this organization.");
  }
  return {
    groupName: (groupResult.data.name ?? "").trim() || "this group",
    tagName: tagResult.data.name,
  };
}

const tagGroupDefinition: ActionDefinition<TagGroupParams> = {
  id: "tag_group",
  label: "Tag group",
  description: "Apply a single tag to a group.",
  requiredTier: "staff",
  paramsSchema: tagGroupParams,
  defaultRequiresConfirm: false,
  async buildPreview(params, ctx) {
    const { groupName, tagName } = await resolveGroupAndTag(
      ctx.organizationId,
      params.group_id,
      params.tag_id,
    );
    return { summary: `Tag ${groupName} with "${tagName}"` };
  },
  async execute(params, ctx): Promise<ActionResult> {
    const { groupName, tagName } = await resolveGroupAndTag(
      ctx.organizationId,
      params.group_id,
      params.tag_id,
    );

    // Idempotent upsert — the unique index on
    // (organization_id, tag_id, taggable_type, taggable_id) means a
    // re-tag is a no-op success, not a 23505.
    const { error } = await supabase.from("taggings").upsert(
      {
        organization_id: ctx.organizationId,
        tag_id: params.tag_id,
        taggable_type: "Group",
        taggable_id: params.group_id,
      },
      { onConflict: "organization_id,tag_id,taggable_type,taggable_id" },
    );

    if (error) {
      return {
        ok: false,
        summary: `Failed to tag ${groupName}`,
        error: error.message,
      };
    }

    const audit = await logAudit({
      action: "tag.apply",
      tableName: "taggings",
      recordId: params.group_id,
      organizationId: ctx.organizationId,
      userId: ctx.userId,
      newData: {
        tag_id: params.tag_id,
        tag_name: tagName,
        group_id: params.group_id,
        taggable_type: "Group",
        source: ctx.source,
        reason: ctx.reason,
      },
    });

    return {
      ok: true,
      summary: `Tagged ${groupName} with "${tagName}"`,
      auditLogId: audit?.id ?? null,
      inverse: {
        actionId: "untag_group",
        params: { group_id: params.group_id, tag_id: params.tag_id },
        label: `Undo: remove "${tagName}"`,
      },
    };
  },
};

const untagGroupDefinition: ActionDefinition<TagGroupParams> = {
  id: "untag_group",
  label: "Remove tag from group",
  description: "Remove a single tag from a group.",
  requiredTier: "staff",
  paramsSchema: tagGroupParams,
  defaultRequiresConfirm: false,
  async buildPreview(params, ctx) {
    const { groupName, tagName } = await resolveGroupAndTag(
      ctx.organizationId,
      params.group_id,
      params.tag_id,
    );
    return { summary: `Remove "${tagName}" from ${groupName}` };
  },
  async execute(params, ctx): Promise<ActionResult> {
    const { groupName, tagName } = await resolveGroupAndTag(
      ctx.organizationId,
      params.group_id,
      params.tag_id,
    );

    const { error } = await supabase
      .from("taggings")
      .delete()
      .eq("organization_id", ctx.organizationId)
      .eq("tag_id", params.tag_id)
      .eq("taggable_type", "Group")
      .eq("taggable_id", params.group_id);

    if (error) {
      return {
        ok: false,
        summary: `Failed to remove tag from ${groupName}`,
        error: error.message,
      };
    }

    const audit = await logAudit({
      action: "tag.remove",
      tableName: "taggings",
      recordId: params.group_id,
      organizationId: ctx.organizationId,
      userId: ctx.userId,
      oldData: { tag_id: params.tag_id, tag_name: tagName, group_id: params.group_id },
      newData: { taggable_type: "Group", source: ctx.source, reason: ctx.reason },
    });

    return {
      ok: true,
      summary: `Removed "${tagName}" from ${groupName}`,
      auditLogId: audit?.id ?? null,
      inverse: {
        actionId: "tag_group",
        params: { group_id: params.group_id, tag_id: params.tag_id },
        label: `Undo: re-apply "${tagName}"`,
      },
    };
  },
};

registerAction(tagGroupDefinition);
registerAction(untagGroupDefinition);

export { tagGroupDefinition, untagGroupDefinition };
